import React from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { Tv, Users, Home as HomeIcon, Menu, X, LogOut, Shield } from 'lucide-react';
import StreamHub from './pages/StreamHub';
import PartyManager from './pages/PartyManager';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import ProtectedRoute from './components/ProtectedRoute';
import pb from './pocketbase';
import './App.css';

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [user, setUser] = React.useState(pb.authStore.model);

  React.useEffect(() => {
    return pb.authStore.onChange((token, model) => {
      setUser(model);
    });
  }, []);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
      <Router>
        <div style={styles.container}>
          {/* BOUTON BURGER MOBILE */}
          {user && <button
              style={styles.burgerBtn}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>}

          {/* OVERLAY MOBILE */}
          {mobileMenuOpen && (
              <div
                  style={styles.mobileOverlay}
                  onClick={closeMobileMenu}
              />
          )}

          {/* SIDEBAR GAUCHE */}
          {user && <nav style={{
            ...styles.sidebar,
            ...(mobileMenuOpen ? styles.sidebarMobileOpen : {})
          }}>
            <div style={styles.logo} className="tvs-title-gradient">🐿️ TVS</div>
            <NavLink
                to="/"
                className="tvs-btn-lift"
                style={({isActive}) => isActive ? styles.activeLink : styles.link}
                onClick={closeMobileMenu}
            >
              <HomeIcon size={20} /> Accueil
            </NavLink>
            <NavLink
                to="/parties"
                className="tvs-btn-lift"
                style={({isActive}) => isActive ? styles.activeLink : styles.link}
                onClick={closeMobileMenu}
            >
              <Users size={20} /> Organisation
            </NavLink>
            <NavLink
                to="/streams"
                className="tvs-btn-lift"
                style={({isActive}) => isActive ? styles.activeLink : styles.link}
                onClick={closeMobileMenu}
            >
              <Tv size={20} /> Streams
            </NavLink>
            {user?.isAdmin && (
              <NavLink
                  to="/admin"
                  className="tvs-btn-lift"
                  style={({isActive}) => isActive ? styles.activeLink : styles.link}
                  onClick={closeMobileMenu}
              >
                <Shield size={20} /> Utilisateurs
              </NavLink>
            )}
            <div style={styles.adminToggleWrapper}>
              <LogoutButton />
            </div>
          </nav>}

          {/* CONTENU DROIT */}
          <main style={styles.content}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<ProtectedRoute><Welcome user={user} /></ProtectedRoute>} />
              <Route path="/streams" element={<ProtectedRoute><StreamHub /></ProtectedRoute>} />
              <Route path="/parties" element={<ProtectedRoute><PartyManager isGlobalAdmin={user?.isAdmin || false} /></ProtectedRoute>} />
              <Route path="/parties/:partyId" element={<ProtectedRoute><PartyManager isGlobalAdmin={user?.isAdmin || false} /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            </Routes>
          </main>
        </div>
      </Router>
  );
}

const LogoutButton = () => {
  const navigate = useNavigate();
  const handleLogout = () => {
    pb.authStore.clear();
    navigate('/login');
  };
  return (
    <button onClick={handleLogout} style={styles.logoutBtn}>
      <LogOut size={16} /> Déconnexion
    </button>
  );
};

const NavTile = ({ to, icon, label, description, color = '#ff4444', badge, index = 0 }) => {
  const [hovered, setHovered] = React.useState(false);
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(to)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="fade-in-up"
      style={{
        position: 'relative',
        background: hovered
            ? `linear-gradient(145deg, #1c1c1c, #202020)`
            : 'linear-gradient(145deg, #161616, #191919)',
        border: `1px solid ${hovered ? color : '#2a2a2a'}`,
        borderRadius: '20px',
        padding: '32px 24px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '14px',
        transition: 'transform 0.25s cubic-bezier(.2,.8,.2,1), box-shadow 0.25s, border-color 0.25s, background 0.25s',
        boxShadow: hovered ? `0 16px 32px ${color}29` : '0 4px 12px rgba(0,0,0,0.25)',
        transform: hovered ? 'translateY(-6px)' : 'none',
        minHeight: '190px',
        animationDelay: `${index * 90}ms`,
      }}
    >
      {badge && (
        <div style={{
          position: 'absolute',
          top: '14px',
          right: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '0.7rem',
          fontWeight: 'bold',
          color: badge.live ? '#ff6b6b' : '#555',
        }}>
          {badge.live && (
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#ff4444',
              animation: 'pulse 2s infinite',
            }} />
          )}
          {badge.text}
        </div>
      )}
      <div style={{
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: hovered ? `${color}33` : `${color}1a`,
        color: color,
        transition: 'background-color 0.25s',
      }}>
        {icon}
      </div>
      <span style={{ color: 'white', fontWeight: '700', fontSize: '1.05rem' }}>{label}</span>
      {description && (
        <span style={{ color: '#777', fontSize: '0.78rem', textAlign: 'center' }}>{description}</span>
      )}
    </div>
  );
};

const Welcome = ({ user }) => {
  const [liveCount, setLiveCount] = React.useState(0);

  React.useEffect(() => {
    const fetchLive = async () => {
      try {
        const res = await fetch('https://theviolentsquirrels.fr/api/paths/list');
        const data = await res.json();
        const count = (data.items || []).filter(item => item.ready === true).length;
        setLiveCount(count);
      } catch (err) {
        // Pas grave, le badge restera à 0
      }
    };
    fetchLive();
    const interval = setInterval(fetchLive, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ padding: 'clamp(40px, 8vw, 80px) 24px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute',
        top: '-120px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '480px',
        height: '320px',
        background: 'radial-gradient(circle, rgba(255,68,68,0.25) 0%, rgba(255,68,68,0) 70%)',
        animation: 'heroGlow 4s ease-in-out infinite',
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', fontSize: '3.2rem', marginBottom: '8px' }}>🐿️</div>
      <h1 style={{
        margin: '0 0 10px 0',
        fontSize: 'clamp(1.8rem, 5vw, 2.6rem)',
        fontWeight: 800,
        letterSpacing: '1px',
        background: 'linear-gradient(135deg, #ff6b6b, #ff4444 60%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        position: 'relative',
      }}>
        The Violent Squirrels
      </h1>
      <p style={{ color: '#777', marginBottom: '48px', fontSize: '0.95rem', position: 'relative' }}>
        {user?.username ? `Content de te revoir, ${user.username}.` : 'Hub communautaire'}
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: '22px',
        maxWidth: '760px',
        margin: '0 auto',
        position: 'relative',
      }}>
        <NavTile
            to="/streams"
            icon={<Tv size={28} />}
            label="Streams"
            description="Regarder les diffusions en direct"
            color="#ff4444"
            badge={liveCount > 0 ? { live: true, text: `${liveCount} en ligne` } : { live: false, text: 'Hors ligne' }}
            index={0}
        />
        <NavTile
            to="/parties"
            icon={<Users size={28} />}
            label="Organisation"
            description="Planifier vos sessions de jeu"
            color="#4d8dff"
            index={1}
        />
        {user?.isAdmin && (
          <NavTile
              to="/admin"
              icon={<Shield size={28} />}
              label="Utilisateurs"
              description="Gérer les comptes de la squad"
              color="#ffb347"
              index={2}
          />
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#0f0f0f',
    color: 'white',
    flexDirection: 'row',
    position: 'relative'
  },
  burgerBtn: {
    display: 'none',
    position: 'fixed',
    top: '15px',
    left: '15px',
    zIndex: 1001,
    background: '#ff4444',
    border: 'none',
    borderRadius: '8px',
    padding: '10px',
    color: 'white',
    cursor: 'pointer',
    boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
  },
  mobileOverlay: {
    display: 'none',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 999
  },
  sidebar: {
    width: '240px',
    backgroundColor: '#161616',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    borderRight: '1px solid #333',
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflowY: 'auto',
    transition: 'transform 0.3s ease',
    zIndex: 1000
  },
  sidebarMobileOpen: {
    transform: 'translateX(0)'
  },
  logo: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#ff4444',
    marginBottom: '30px',
    textAlign: 'center'
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#aaa',
    textDecoration: 'none',
    padding: '12px',
    borderRadius: '8px',
    transition: 'all 0.2s'
  },
  activeLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: 'white',
    textDecoration: 'none',
    padding: '12px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #ff6b6b, #ff4444)',
    boxShadow: '0 4px 14px rgba(255, 68, 68, 0.35)'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  adminToggleWrapper: {
    marginTop: 'auto',
    paddingTop: '20px'
  },
  logoutBtn: {
    width: '100%',
    padding: '10px',
    background: 'transparent',
    color: '#888',
    border: '1px solid #333',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginBottom: '8px',
  }
};

export default App;
